import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'panchami_currency';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const currencyConfig = {
  INR: { code: 'INR', label: 'INR', locale: 'en-IN' },
  USD: { code: 'USD', label: 'USD', locale: 'en-US' },
  EUR: { code: 'EUR', label: 'EUR', locale: 'de-DE' },
  GBP: { code: 'GBP', label: 'GBP', locale: 'en-GB' },
  AED: { code: 'AED', label: 'AED', locale: 'en-AE' },
  SGD: { code: 'SGD', label: 'SGD', locale: 'en-SG' },
};

const fallbackRates = {
  INR: 1,
  USD: 0.012,
  EUR: 0.011,
  GBP: 0.0094,
  AED: 0.044,
  SGD: 0.016,
};

const CurrencyContext = createContext(null);

export const supportedCurrencies = Object.values(currencyConfig);

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState('INR');
  const [rates, setRates] = useState(fallbackRates);
  const [rateSource, setRateSource] = useState('fallback');
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    const savedCurrency = window.localStorage.getItem(STORAGE_KEY);
    if (savedCurrency && currencyConfig[savedCurrency]) {
      setCurrency(savedCurrency);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadRates() {
      try {
        const response = await fetch(`${BACKEND_URL}/api/exchange-rates`);
        if (!response.ok) {
          throw new Error('Failed to fetch exchange rates');
        }

        const data = await response.json();
        if (!isMounted) return;

        setRates({ ...fallbackRates, ...data.rates });
        setRateSource(data.source || 'live');
        setUpdatedAt(data.updated_at || null);
      } catch (error) {
        if (!isMounted) return;
        setRates(fallbackRates);
        setRateSource('fallback');
        setUpdatedAt(null);
      }
    }

    loadRates();
    const refreshTimer = window.setInterval(loadRates, 60 * 60 * 1000);

    return () => {
      isMounted = false;
      window.clearInterval(refreshTimer);
    };
  }, []);

  const value = useMemo(() => {
    const selected = currencyConfig[currency] || currencyConfig.INR;
    const rate = rates[selected.code] || 1;

    return {
      currency,
      supportedCurrencies,
      rateSource,
      updatedAt,
      setCurrency: (nextCurrency) => {
        if (!currencyConfig[nextCurrency]) return;
        setCurrency(nextCurrency);
        window.localStorage.setItem(STORAGE_KEY, nextCurrency);
      },
      convertFromInr: (amount) => {
        const numericAmount = Number(amount) || 0;
        return numericAmount * rate;
      },
      formatPrice: (amount) => {
        const convertedAmount = (Number(amount) || 0) * rate;
        return new Intl.NumberFormat(selected.locale, {
          style: 'currency',
          currency: selected.code,
          maximumFractionDigits: selected.code === 'INR' ? 0 : 2,
        }).format(convertedAmount);
      },
      isBaseCurrency: selected.code === 'INR',
    };
  }, [currency, rates, rateSource, updatedAt]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
}
